import {
  Breadcrumbs,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { FormField } from "@/components/core/form-field";
import { CardLoadingSkeleton, LoadingSkeleton } from "@/components/core/loading-skeleton";
import { Link } from "@/components/core/tanstack-link";
import { DownloadInvoice } from "@/components/invoices/download-invoice";
import { NotFound } from "@/components/not-found";
import { formatCurrency, formatDate } from "@/helpers/intl";
import {
  InvoiceStatus,
  portalInvoiceDownload,
  usePortal,
  usePortalInvoice,
} from "@/models/backend";

export const Route = createFileRoute("/_portal/my-invoices/$invoiceId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { invoiceId } = Route.useParams();
  const { token } = Route.useSearch();
  const { data: customer, isError, error } = usePortal({ token });
  const { data: invoice, isLoading } = usePortalInvoice(invoiceId, { token });
  const downloadInvoice = portalInvoiceDownload;
  if (isError && "status" in error && error.status === 404) {
    return <NotFound />;
  }

  if (isLoading) {
    return <CardLoadingSkeleton />;
  }
  return (
    <Stack spacing={4}>
      <Breadcrumbs separator=">" aria-label="breadcrumb">
        <Link to="/my-invoices" search={{ token }}>
          All invoices
        </Link>
        <Typography>{invoice?.number}</Typography>
      </Breadcrumbs>
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <Typography variant="h2">{invoice?.number}</Typography>
        {invoice?.status === InvoiceStatus.overdue ? (
          <Chip sx={{ ml: "auto" }} color="warning" variant="outlined" label="Invoice overdue" />
        ) : invoice?.status === InvoiceStatus.posted ? (
          <Chip
            sx={{ ml: "auto" }}
            color="secondary"
            variant="outlined"
            label="Invoice outstanding"
          />
        ) : (
          <Chip
            sx={{ ml: "auto" }}
            color="primary"
            variant="outlined"
            label="Payments up to date"
          />
        )}
      </Stack>
      {!invoice ? (
        <LoadingSkeleton />
      ) : (
        <DownloadInvoice
          invoice={invoice}
          download={() => downloadInvoice(invoiceId, { token }, { responseType: "blob" })}
        />
      )}
      <Stack spacing={2}>
        <Typography variant="h4">Summary</Typography>
        <Grid container sx={{ pl: "16px" }}>
          <Grid size={6}>
            <Stack spacing={3}>
              <FormField label="Billed to" component={Typography}>
                {customer?.name}
              </FormField>
              <FormField label="Invoice number" component={Typography}>
                {invoice?.number}
              </FormField>
              <FormField label="Due date" component={Typography}>
                {formatDate(invoice?.due)}
              </FormField>
            </Stack>
          </Grid>
          <Grid size={6}>
            <FormField label="From" component={Typography}>
              {customer?.organization.name}
            </FormField>
          </Grid>
        </Grid>
      </Stack>
      {invoice?.line_items != null && (
        <Stack spacing={2}>
          <Typography variant="h4">Items</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                {invoice.line_items?.[0]?.amount_tax != null && (
                  <>
                    <TableCell>Amount</TableCell>
                    <TableCell>Tax</TableCell>
                  </>
                )}
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice?.line_items?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.description}</TableCell>
                  {invoice.line_items?.[0]?.amount_tax != null && (
                    <>
                      <TableCell>
                        {formatCurrency(
                          item.total_amount - (item.amount_tax ?? 0),
                          invoice.currency,
                        )}
                      </TableCell>
                      <TableCell>{formatCurrency(item.amount_tax, invoice.currency)}</TableCell>
                    </>
                  )}
                  <TableCell>{formatCurrency(item.total_amount, invoice.currency)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  colSpan={invoice.line_items?.[0]?.amount_tax != null ? 3 : 1}
                  sx={{ fontWeight: "bold" }}
                >
                  Total amount
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {formatCurrency(invoice.total_amount, invoice.currency)}
                </TableCell>
              </TableRow>
              {invoice.amount_due !== invoice.total_amount && (
                <>
                  <TableRow>
                    <TableCell
                      colSpan={invoice.line_items?.[0]?.amount_tax != null ? 3 : 1}
                      sx={{ fontWeight: "bold" }}
                    >
                      Amount paid
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {formatCurrency(-invoice.amount_paid, invoice.currency)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={invoice.line_items?.[0]?.amount_tax != null ? 3 : 1}
                      sx={{ fontWeight: "bold" }}
                    >
                      Balance due
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {formatCurrency(invoice.amount_due, invoice.currency)}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </Stack>
      )}
    </Stack>
  );
}
